import { api } from '../api.ts';
import { escapeHtml, html, mount, th } from '../html.ts';
import { formatDateTime, options, t, tk } from '../i18n.ts';
import { confirmDialog, emptyState, field, openFormDialog, optionList, select, textarea, toast, toastError, badge } from '../ui.ts';

interface Presets {
  outbound: any[];
  inbound: any[];
}

let presetCache: Promise<Presets> | null = null;
const presets = () => (presetCache ??= api.get<Presets>('/connector-presets'));

function parseJson(label: string, text: unknown): unknown {
  try {
    return JSON.parse(String(text ?? ''));
  } catch {
    throw new Error(t('{label} ist kein gültiges JSON', { label }));
  }
}

/** "Name: Wert" per line → header object. */
function parseHeaders(text: unknown): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const line of String(text ?? '').split('\n')) {
    const index = line.indexOf(':');
    if (index <= 0) continue;
    headers[line.slice(0, index).trim()] = line.slice(index + 1).trim();
  }
  return headers;
}

function secretBox(title: string, rows: [string, string][], hint: string) {
  openFormDialog({
    title,
    description: hint,
    fields: html`${rows.map(
      ([label, value]) => html`<label class="p-field pma-span-2"><span class="p-label">${label}</span><input class="p-input pma-mono" readonly value="${value}"></label>`,
    )}`,
    submitLabel: t('Schließen'),
    onSubmit: async () => {},
  });
}

// ------------------------------------------------------------------ outbound

export async function openWebhookDialog(eventTypes: string[], onDone: () => Promise<void>) {
  const { outbound } = await presets();
  const presetOptions = [
    options({ pma: t('PMA-JSON, signiert (eigene Dienste)'), slack: t('Slack (Incoming Webhook)') }),
    ...outbound.map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(t(p.name))}</option>`),
  ].join('');
  openFormDialog({
    title: t('Ausgehende Verbindung'),
    description: t('Ereignisse aus PMA an ein anderes System senden. Vorlagen füllen Methode, Header und JSON-Körper vor; Platzhalter wie {{summary}} oder {{data.after.title}} werden beim Senden ersetzt.'),
    wide: true,
    fields: html`${select(t('Vorlage'), 'preset', presetOptions, { span: true })}
      <p class="pma-span-2 pma-muted pma-small" data-preset-hint></p>
      ${field(t('Name'), 'name', 'text', { required: true, placeholder: t('z. B. Teams-Kanal Projekt') })}
      ${select(t('Methode'), 'method', '<option>POST</option><option>PUT</option><option>PATCH</option>')}
      ${field(t('Ziel-URL'), 'url', 'url', { required: true, placeholder: t('https://…'), span: true })}
      <div class="pma-span-2" data-custom hidden>
        ${textarea(t('Header (eine Zeile pro Header: Name: Wert)'), 'headers', { rows: 3, hint: t('Werte werden gespeichert, aber nie wieder angezeigt.') })}
        ${textarea(t('JSON-Körper (Vorlage)'), 'body_template', { rows: 8 })}
      </div>
      <fieldset class="p-fieldset pma-span-2"><legend class="p-label">${t('Ereignisse')}</legend>
        <label class="p-check"><input type="checkbox" name="all" checked> <span>${t('Alle Ereignisse (*)')}</span></label>
        <div class="pma-event-grid">${eventTypes.filter((x) => x !== 'webhook.test').map(
          (x) => html`<label class="p-check pma-small"><input type="checkbox" name="evt:${x}"> <span>${x}</span></label>`,
        )}</div></fieldset>`,
    submitLabel: t('Anlegen'),
    onOpen: (dialog) => {
      const form = dialog.querySelector('form')!;
      const presetSelect = form.elements.namedItem('preset') as HTMLSelectElement;
      const apply = () => {
        const preset = outbound.find((p) => p.id === presetSelect.value);
        dialog.querySelector<HTMLElement>('[data-custom]')!.hidden = !preset;
        dialog.querySelector<HTMLElement>('[data-preset-hint]')!.textContent =
          (preset?.hint ? t(preset.hint) : undefined) ?? (presetSelect.value === 'slack' ? t('Incoming-Webhook-URL aus der Slack-App.') : t('Signierter JSON-Körper; Prüfung per x-pma-signature.'));
        if (!preset) return;
        (form.elements.namedItem('method') as HTMLSelectElement).value = preset.method ?? 'POST';
        (form.elements.namedItem('body_template') as HTMLTextAreaElement).value = JSON.stringify(preset.body_template, null, 2);
        (form.elements.namedItem('headers') as HTMLTextAreaElement).value = Object.entries(preset.headers ?? {})
          .map(([k, v]) => `${k}: ${v}`)
          .join('\n');
        const all = form.elements.namedItem('all') as HTMLInputElement;
        all.checked = !preset.event_types;
        for (const type of preset.event_types ?? []) {
          const box = form.elements.namedItem(`evt:${type}`) as HTMLInputElement | null;
          if (box) box.checked = true;
        }
      };
      presetSelect.addEventListener('change', apply);
      apply();
    },
    onSubmit: async (data) => {
      const selected = Object.entries(data).filter(([k, v]) => k.startsWith('evt:') && v === true).map(([k]) => k.slice(4));
      const preset = String(data.preset);
      const custom = preset !== 'pma' && preset !== 'slack';
      const body: Record<string, unknown> = {
        name: data.name,
        url: data.url,
        format: custom ? 'custom' : preset,
        event_types: data.all || !selected.length ? ['*'] : selected,
      };
      if (custom) {
        body.method = data.method;
        body.headers = parseHeaders(data.headers);
        body.body_template = parseJson(t('Der JSON-Körper'), data.body_template);
      }
      const created = await api.post('/webhooks', body);
      await onDone();
      if (created.format === 'pma') {
        secretBox(t('Webhook angelegt'), [[t('Signatur-Secret'), created.secret]], t('Mit diesem Secret prüft der Empfänger die Signatur. Es wird nur jetzt angezeigt.'));
      } else toast(t('Verbindung angelegt'), t('Mit „Testen“ prüfst du sie.'));
    },
  });
}

// ------------------------------------------------------------------ inbound

const ACTION_LABEL: Record<string, string> = {
  upsert_task: tk('Aufgabe anlegen/aktualisieren'),
  create_risk: tk('Risiko anlegen/aktualisieren'),
  add_note: tk('Notiz anlegen'),
  record_kpi: tk('Kennzahl-Messwert erfassen'),
};
const actionLabel = (action: string) => t(ACTION_LABEL[action] ?? action);

async function openInboundDialog(onDone: () => Promise<void>) {
  const [{ inbound }, projects] = await Promise.all([presets(), api.get('/projects')]);
  openFormDialog({
    title: t('Eingehender Connector'),
    description: t('Ein anderes System schickt Ereignisse an eine eigene URL; das Mapping übersetzt sie in PMA-Einträge. Wiederholte Lieferungen aktualisieren denselben Eintrag (über die externe ID).'),
    wide: true,
    fields: html`${select(t('Vorlage'), 'preset', inbound.map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(t(p.name))}</option>`).join(''), { span: true })}
      <p class="pma-span-2 pma-muted pma-small" data-preset-hint></p>
      ${field(t('Name'), 'name', 'text', { required: true })}
      ${select(t('Zielprojekt'), 'project_id', optionList(projects.items.map((p: any) => ({ id: p.id, label: `${p.key} · ${p.name}` })), undefined, t('Bitte wählen')), { required: true })}
      ${select(t('Aktion'), 'action', options(Object.fromEntries(Object.keys(ACTION_LABEL).map((key) => [key, actionLabel(key)]))))}
      ${field(t('System-Kürzel'), 'external_system', 'text', { required: true, hint: t('z. B. github, jira, alerts'), attrs: 'pattern="[a-z][a-z0-9_-]{1,31}"' })}
      ${select(t('Betriebsart'), 'mode', options({ push: t('Empfang: das System sendet an eine PMA-URL'), pull: t('Abruf: PMA holt regelmäßig eine JSON-Quelle') }), { span: true })}
      <div class="pma-span-2 pma-form-grid" data-pull hidden>
        ${field(t('Quelle (URL)'), 'pull_url', 'url', { span: true, placeholder: t('https://…') })}
        ${field(t('Intervall (Minuten)'), 'pull_interval_minutes', 'number', { value: 15, attrs: 'min="5" max="1440"' })}
        ${field(t('Pfad zur Liste'), 'items_path', 'text', { hint: t('z. B. issues; leer = die Antwort selbst') })}
        ${textarea(t('Header (Name: Wert je Zeile)'), 'pull_headers', { rows: 2, hint: t('Werte werden gespeichert, aber nie wieder angezeigt.') })}
      </div>
      ${textarea(t('Mapping (JSON)'), 'mapping', { rows: 12, hint: t('fields: Zielfelder mit Platzhaltern aus dem eingehenden JSON; external_id macht Lieferungen idempotent; when filtert; status.map übersetzt Zustände.') })}`,
    submitLabel: t('Connector anlegen'),
    onOpen: (dialog) => {
      const form = dialog.querySelector('form')!;
      const presetSelect = form.elements.namedItem('preset') as HTMLSelectElement;
      const apply = () => {
        const preset = inbound.find((p) => p.id === presetSelect.value);
        if (!preset) return;
        dialog.querySelector<HTMLElement>('[data-preset-hint]')!.textContent = t(preset.hint);
        (form.elements.namedItem('name') as HTMLInputElement).value = t(preset.name);
        (form.elements.namedItem('action') as HTMLSelectElement).value = preset.action;
        (form.elements.namedItem('external_system') as HTMLInputElement).value = preset.external_system;
        (form.elements.namedItem('mapping') as HTMLTextAreaElement).value = JSON.stringify(preset.mapping, null, 2);
        const mode = form.elements.namedItem('mode') as HTMLSelectElement;
        mode.value = preset.mode ?? 'push';
        (form.elements.namedItem('pull_url') as HTMLInputElement).value = preset.pull_url ?? '';
        (form.elements.namedItem('pull_interval_minutes') as HTMLInputElement).value = String(preset.pull_interval_minutes ?? 15);
        (form.elements.namedItem('items_path') as HTMLInputElement).value = preset.items_path ?? '';
        (form.elements.namedItem('pull_headers') as HTMLTextAreaElement).value = Object.entries(preset.pull_headers ?? {})
          .map(([k, v]) => `${k}: ${v}`)
          .join('\n');
        toggle();
      };
      const toggle = () => {
        dialog.querySelector<HTMLElement>('[data-pull]')!.hidden = (form.elements.namedItem('mode') as HTMLSelectElement).value !== 'pull';
      };
      (form.elements.namedItem('mode') as HTMLSelectElement).addEventListener('change', toggle);
      presetSelect.addEventListener('change', apply);
      apply();
    },
    onSubmit: async (data) => {
      const pull = data.mode === 'pull';
      const created = await api.post('/inbound-connectors', {
        name: data.name,
        project_id: data.project_id,
        action: data.action,
        external_system: data.external_system,
        mapping: parseJson(t('Das Mapping'), data.mapping),
        mode: data.mode,
        ...(pull
          ? {
              pull_url: data.pull_url,
              pull_interval_minutes: data.pull_interval_minutes,
              items_path: data.items_path ?? '',
              pull_headers: parseHeaders(data.pull_headers),
            }
          : {}),
      });
      await onDone();
      if (pull) {
        toast(t('Abruf-Connector angelegt'), t('Mit „Jetzt abrufen“ testest du die Quelle sofort.'));
        return;
      }
      secretBox(
        t('Connector angelegt'),
        [
          [t('Endpunkt'), created.endpoint],
          [t('Token (Header X-PMA-Token)'), created.token],
          [t('URL mit Token (für Dienste ohne eigene Header)'), `${created.endpoint}?token=${created.token}`],
        ],
        t('Das Token wird nur jetzt angezeigt. Dienste senden JSON per POST an den Endpunkt.'),
      );
    },
  });
}

function openPreview(connector: any) {
  openFormDialog({
    title: t('Vorschau: {name}', { name: connector.name }),
    description: t('Beispiel-JSON einfügen, wie es das andere System sendet. Es wird nichts gespeichert.'),
    wide: true,
    fields: html`${textarea(t('Eingehendes JSON'), 'payload', { rows: 10 })}<pre class="pma-span-2 pma-small pma-mono pma-preview" data-result></pre>`,
    submitLabel: t('Mapping anwenden'),
    onSubmit: async (data, form) => {
      const result = await api.post(`/inbound-connectors/${connector.id}/preview`, { payload: parseJson(t('Das Beispiel'), data.payload) });
      form.querySelector('[data-result]')!.textContent = JSON.stringify(result.preview ?? result, null, 2);
      return false;
    },
  });
}

export async function renderInboundSection(target: HTMLElement): Promise<void> {
  const { items } = await api.get<{ items: any[] }>('/inbound-connectors');
  mount(
    target,
    html`<div class="p-card__header"><h2 id="set-inbound">${t('Eingehende Connectoren')}</h2>
        <button class="p-button p-button--primary p-button--sm" type="button" data-new-inbound>${t('Connector hinzufügen')}</button></div>
      <div class="p-card__body">
        <p class="pma-small">${th(
          t('Andere Systeme legen Aufgaben, Risiken, Notizen oder Messwerte an – per {push} über eine eigene URL (GitHub, GitLab, Monitoring, Skripte) oder per {pull} einer JSON-Quelle im Intervall (Jira-Suche, Statusseiten, eigene APIs).'),
          { push: html`<strong>${t('Empfang')}</strong>`, pull: html`<strong>${t('Abruf')}</strong>` },
        )}</p>
        ${
          items.length
            ? html`<ul class="pma-list">${items.map(
                (c) => html`<li class="pma-list__row"><div><strong>${c.name}</strong> ${c.active ? '' : badge(t('Pausiert'), 'warning')}
                  <div class="pma-muted pma-small">${actionLabel(c.action)} · ${t('Projekt')} ${c.project_key} · ${t('System')} ${c.external_system} · ${
                    c.mode === 'pull' ? t('Abruf alle {n} min', { n: c.pull_interval_minutes }) : t('Empfang')
                  }</div>
                  <div class="pma-muted pma-small pma-break">${c.mode === 'pull' ? c.pull_url : c.endpoint}</div>
                  <div class="pma-muted pma-small">${t('{n} empfangen', { n: c.received_count })}${c.last_received_at ? ` · ${t('zuletzt {when}', { when: formatDateTime(c.last_received_at) })}` : ''}${
                    c.last_error ? html` · <span class="pma-overdue">${t('Fehler')}: ${c.last_error}</span>` : ''
                  }</div></div>
                  <div class="pma-list__meta">
                    ${c.mode === 'pull' ? html`<button class="p-button p-button--ghost p-button--sm" type="button" data-pull-now="${c.id}">${t('Jetzt abrufen')}</button>` : ''}
                    <button class="p-button p-button--ghost p-button--sm" type="button" data-preview="${c.id}">${t('Vorschau')}</button>
                    <button class="p-button p-button--ghost p-button--sm" type="button" data-toggle="${c.id}" data-active="${c.active}">${c.active ? t('Pausieren') : t('Aktivieren')}</button>
                    <button class="p-button p-button--ghost p-button--sm pma-danger-text" type="button" data-delete="${c.id}">${t('Löschen')}</button>
                  </div></li>`,
              )}</ul>`
            : emptyState(t('Keine eingehenden Connectoren'), t('Beginne mit einer Vorlage, z. B. GitHub Issues → Aufgaben.'))
        }
      </div>`,
  );
  const reload = () => renderInboundSection(target);
  target.onclick = async (event) => {
    const element = event.target as HTMLElement;
    try {
      if (element.closest('[data-new-inbound]')) await openInboundDialog(reload);
      const pullNow = element.closest<HTMLButtonElement>('[data-pull-now]');
      if (pullNow) {
        pullNow.disabled = true;
        const result = await api.post(`/inbound-connectors/${pullNow.dataset.pullNow}/pull`);
        toast(
          result.error ? t('Abruf mit Fehler') : t('Abruf abgeschlossen'),
          t('{items} Einträge: {created} neu, {updated} aktualisiert, {skipped} übersprungen, {failed} fehlgeschlagen', result) + (result.error ? ` – ${result.error}` : ''),
        );
        await reload();
      }
      const preview = element.closest<HTMLElement>('[data-preview]')?.dataset.preview;
      if (preview) openPreview(items.find((c) => c.id === preview));
      const toggle = element.closest<HTMLElement>('[data-toggle]');
      if (toggle) {
        await api.patch(`/inbound-connectors/${toggle.dataset.toggle}`, { active: toggle.dataset.active !== 'true' });
        await reload();
      }
      const remove = element.closest<HTMLElement>('[data-delete]')?.dataset.delete;
      if (remove && (await confirmDialog(t('Connector löschen?'), t('Die URL funktioniert danach nicht mehr. Bereits angelegte Einträge bleiben erhalten.')))) {
        await api.delete(`/inbound-connectors/${remove}`);
        await reload();
      }
    } catch (error) {
      toastError(error);
    }
  };
}
